import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Alert, Badge, Button, Card, Field, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { formatDate, formatMoney, today } from '../people/data';
import { PeopleNav } from '../people/PeopleNav';
import { type Assignment, canSeeLicenses, type LicenseDetail } from './data';
import { LicenseForm } from './LicenseForm';
import { PersonPicker } from './PersonPicker';

/** Detalle de una licencia: su costo y puestos, quién la tiene, y (si la administra) asignarla, liberarla y editarla. */
export function LicenseDetailPage() {
  const { id = '' } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const license = useQuery({ queryKey: ['licencias', 'detalle', id], queryFn: () => api<LicenseDetail>(`/api/licencias/${id}`) });
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (!license.data) return license.error ? <Alert>{errorMessage(license.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const l = license.data;
  const active = l.assignments.filter((a) => a.active);
  const past = l.assignments.filter((a) => !a.active);

  async function patch(body: object, message: string) {
    await api(`/api/licencias/${l.id}`, { method: 'PATCH', json: body });
    await qc.invalidateQueries({ queryKey: ['licencias'] });
    setNotice(message);
  }

  return (
    <div className="space-y-4">
      <div>
        {canSeeLicenses(me) && (
          <Link to="/licencias" className="text-sm text-marca hover:underline">
            ← {t('li.titulo')}
          </Link>
        )}
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          {l.name}
          {!l.active && (
            <span className="ml-2 align-middle">
              <Badge>{t('li.inactiva')}</Badge>
            </span>
          )}
        </h1>
        <p className="text-sm text-slate-600">
          {t(l.billing === 'mensual' ? 'li.mensual' : 'li.anual')}
          {l.shared && ` · ${t('li.compartida')}`}
          {l.platform && ` · ${l.platform}`}
        </p>
      </div>
      {canSeeLicenses(me) && <PeopleNav />}
      {notice && <Alert tone="success">{notice}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <Card title={t('li.asignadas', { n: active.length })}>
            {active.length === 0 ? <p className="text-sm text-slate-500">{t('li.sin_asignaciones')}</p> : <AssignmentsTable license={l} rows={active} canRelease={l.can.manage} />}
          </Card>
          {past.length > 0 && (
            <Card title={t('li.historial')}>
              <AssignmentsTable license={l} rows={past} canRelease={false} />
            </Card>
          )}
        </div>
        <div className="space-y-4">
          <Card variant="aside" title={t('li.resumen')}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-slate-500">{l.billing === 'mensual' ? t('li.campo_costo_mes') : t('li.campo_costo_anio')}</dt>
              <dd className="tabular-nums">{formatMoney(l.baseCost)}</dd>
              <dt className="text-slate-500">{t('li.col_puestos')}</dt>
              <dd className="tabular-nums">{l.seatsPurchased === null ? t('li.puestos_sin_tope', { asignados: l.assigned }) : t('li.puestos_de', { asignados: l.assigned, total: l.seatsPurchased })}</dd>
              {l.costs.available !== null && (
                <>
                  <dt className="text-slate-500">{t('li.libres')}</dt>
                  <dd className="tabular-nums">{l.costs.available}</dd>
                </>
              )}
              <dt className="text-slate-500">{t('li.col_mes')}</dt>
              <dd className="tabular-nums">{formatMoney(l.costs.monthly)}</dd>
              <dt className="text-slate-500">{t('li.col_anio')}</dt>
              <dd className="tabular-nums">{formatMoney(l.costs.annual)}</dd>
              {l.renewalDate && (
                <>
                  <dt className="text-slate-500">{t('li.col_renovacion')}</dt>
                  <dd>
                    {formatDate(l.renewalDate)}
                    {l.costs.renewalAmount !== null && <span className="block text-xs text-slate-500">{t('li.monto_renovacion', { monto: formatMoney(l.costs.renewalAmount) })}</span>}
                  </dd>
                </>
              )}
              <dt className="text-slate-500">{t('li.puesto_nuevo')}</dt>
              <dd className="tabular-nums">{l.costs.newSeat > 0 ? formatMoney(l.costs.newSeat) : t('us.sin_costo')}</dd>
              <dt className="text-slate-500">{t('li.campo_administrador')}</dt>
              <dd>{l.owner ?? '—'}</dd>
            </dl>
            {l.notes && <p className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-700">{l.notes}</p>}
            {l.can.manage && !editing && (
              <div className="mt-3 flex flex-wrap gap-3 border-t border-slate-100 pt-3">
                <Button variant="link" className="text-sm" onClick={() => setEditing(true)}>
                  {t('li.editar')}
                </Button>
                {l.can.editAll && (
                  <Button variant="link" className="text-sm" onClick={() => void patch({ active: !l.active }, l.active ? t('li.desactivada_ok') : t('li.activada_ok')).catch((err: unknown) => setNotice(errorMessage(err)))}>
                    {l.active ? t('li.desactivar') : t('li.activar')}
                  </Button>
                )}
              </div>
            )}
          </Card>
          {editing && (
            <Card title={t('li.editar')}>
              <LicenseForm
                initial={l}
                ownerOnly={!l.can.editAll}
                submitLabel={t('comun.guardar')}
                onCancel={() => setEditing(false)}
                onSubmit={async (values) => {
                  await patch(values, t('li.guardada_ok'));
                  setEditing(false);
                }}
              />
            </Card>
          )}
          {l.can.manage && l.active && <AssignCard license={l} onDone={setNotice} />}
        </div>
      </div>
    </div>
  );
}

function AssignmentsTable({ license: l, rows, canRelease }: { license: LicenseDetail; rows: Assignment[]; canRelease: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs text-slate-500">
          <tr>
            <th className="py-2 pr-4 font-medium">{t('us.col_persona')}</th>
            <th className="py-2 pr-4 font-medium">{t('li.col_desde')}</th>
            <th className="py-2 pr-4 font-medium">{t('li.col_hasta')}</th>
            {canRelease && <th className="py-2 font-medium">{t('us.col_acciones')}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((a) => (
            <tr key={a.id} className="align-top">
              <td className="py-2 pr-4">
                <span className="font-medium text-slate-900">{a.user ?? '—'}</span>
                <span className="block text-xs text-slate-500">
                  {a.organization}
                  {a.source !== 'manual' && ` · ${t(`li.origen_${a.source}` as 'li.origen_alta')}`}
                  {a.note && ` · ${a.note}`}
                </span>
              </td>
              <td className="py-2 pr-4 text-slate-700">{formatDate(a.startsOn)}</td>
              <td className="py-2 pr-4 text-slate-700">{a.endsOn ? formatDate(a.endsOn) : '—'}</td>
              {canRelease && (
                <td className="py-2">
                  <ReleaseButton license={l} assignment={a} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Liberar (guía 7.6): una mensual deja de costar; una anual vuelve al pool y sigue pagada hasta la renovación. */
function ReleaseButton({ license: l, assignment: a }: { license: LicenseDetail; assignment: Assignment }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [endsOn, setEndsOn] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (a.endsOn) return <span className="text-xs text-slate-500">{t('li.termina_el', { fecha: formatDate(a.endsOn) })}</span>;
  if (!open)
    return (
      <Button variant="link" className="text-sm" onClick={() => setOpen(true)}>
        {t('li.liberar')}
      </Button>
    );
  async function release() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/licencias/asignaciones/${a.id}/liberar`, { json: { endsOn } });
      await qc.invalidateQueries({ queryKey: ['licencias'] });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <div className="w-52 space-y-2">
      {error && <Alert>{error}</Alert>}
      <Field id={`li-hasta-${a.id}`} type="date" label={t('li.ultimo_dia')} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
      <p className="text-xs text-slate-500">{l.billing === 'mensual' ? t('us.deja_mensual') : t('us.deja_anual')}</p>
      <div className="flex gap-2">
        <Button variant="danger" disabled={busy || !endsOn} onClick={() => void release()}>
          {t('li.liberar')}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>
          {t('comun.volver')}
        </Button>
      </div>
    </div>
  );
}

interface NoSeats {
  code: 'no_seats';
  message: string;
  cost: { monthly: number; upfront: number };
}

/** Asignar a una persona. Sin puestos libres, la API pide confirmar la compra de uno más (con su costo). */
function AssignCard({ license: l, onDone }: { license: LicenseDetail; onDone: (message: string) => void }) {
  const qc = useQueryClient();
  const [person, setPerson] = useState<{ id: string; fullName: string } | null>(null);
  const [startsOn, setStartsOn] = useState(today());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noSeats, setNoSeats] = useState<NoSeats | null>(null);

  async function assign(e?: FormEvent, confirmExtraSeat = false) {
    e?.preventDefault();
    if (!person) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ extraSeat: boolean }>(`/api/licencias/${l.id}/asignaciones`, { json: { userId: person.id, startsOn, note: note.trim() || null, confirmExtraSeat } });
      setNoSeats(null);
      onDone(res.extraSeat ? t('li.asignada_extra_ok', { nombre: person.fullName }) : t('li.asignada_ok', { nombre: person.fullName }));
      setPerson(null);
      setNote('');
      await qc.invalidateQueries({ queryKey: ['licencias'] });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as Partial<NoSeats> | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'no_seats') setNoSeats(body as NoSeats);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('li.asignar')}>
      <form onSubmit={(e) => void assign(e)} className="space-y-3">
        {error && <Alert>{error}</Alert>}
        <PersonPicker id="li-persona" label={t('us.campo_persona')} value={person} onChange={setPerson} />
        <Field id="li-desde" type="date" label={t('li.col_desde')} required value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        <Field id="li-nota" label={t('li.nota')} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        {noSeats ? (
          <Alert tone="warning">
            <p>{noSeats.message}</p>
            <p className="mt-1 font-medium">
              {noSeats.cost.monthly > 0 ? t('us.por_mes', { monto: formatMoney(noSeats.cost.monthly) }) : t('li.costo_puesto_extra', { monto: formatMoney(noSeats.cost.upfront) })}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button disabled={busy} onClick={() => void assign(undefined, true)}>
                {t('li.comprar_puesto')}
              </Button>
              <Button variant="secondary" onClick={() => setNoSeats(null)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        ) : (
          <Button type="submit" disabled={busy || !person || !startsOn}>
            {t('li.asignar')}
          </Button>
        )}
      </form>
    </Card>
  );
}
